import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import type { PlayerEpicRun } from "../../types/epicLeaderboard";
import { getEpicColor } from "../../utils/epicColors";
import { formatDate, formatSeconds } from "./format";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Past this many runs, point markers just add noise to the line.
const MANY_RUNS = 60;

const baseOptions: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 2,
  scales: {
    x: {
      ticks: { color: "rgb(199, 238, 255)", maxTicksLimit: 4, maxRotation: 0 },
      grid: { color: "rgb(70, 130, 180)" }
    },
    y: {
      // Lower is better, so don't force the axis down to zero - the
      // interesting movement is a few seconds either way.
      ticks: {
        color: "rgb(199, 238, 255)",
        maxTicksLimit: 5,
        callback: (value) => formatSeconds(Number(value))
      },
      grid: { color: "rgb(70, 130, 180)" }
    }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) =>
          `${ctx.dataset.label}: ${
            ctx.parsed.y === null ? "-" : formatSeconds(ctx.parsed.y)
          }`
      }
    }
  }
};

// Short month + year for the x ticks (the tooltip keeps the full date), so
// four of them fit side by side on a phone.
const formatTick = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" });

type Props = {
  epicName: string;
  runs: PlayerEpicRun[];
};

// A player's clear times over time, oldest to newest.
const PlayerEpicTimesChart = ({ epicName, runs }: Props) => {
  const color = getEpicColor(epicName);
  const manyRuns = runs.length > MANY_RUNS;
  const options: ChartOptions<"line"> = {
    ...baseOptions,
    scales: {
      ...baseOptions.scales,
      x: {
        ...baseOptions.scales?.x,
        ticks: {
          ...baseOptions.scales?.x?.ticks,
          callback: (value) => {
            const run = runs[Number(value)];
            return run ? formatTick(run.runDate) : "";
          }
        }
      }
    }
  };

  return (
    <Line
      options={options}
      data={{
        labels: runs.map((r) => formatDate(r.runDate)),
        datasets: [
          {
            label: "Clear time",
            data: runs.map((r) => r.seconds),
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            pointRadius: manyRuns ? 0 : 3,
            pointHoverRadius: 4,
            tension: 0.2
          }
        ]
      }}
    />
  );
};

export default PlayerEpicTimesChart;
